import { ReactNode } from 'react';
import { Outlet, useParams } from 'react-router-dom';
import { useProducerById, ProducerBusinessStatus } from '@/hooks/useMyProducers';
import { ProducerTermsGate } from './ProducerTermsGate';
import { ProducerStatusBlock } from './ProducerStatusBlock';
import { SupportPreviewBanner } from '@/components/admin/SupportPreviewBanner';
import { useSupportPreview } from '@/context/SupportPreviewContext';
import { Skeleton } from '@/components/ui/skeleton';

/**
 * Wrapper component that applies ProducerTermsGate and ProducerStatusBlock
 * to all producer portal routes.
 * 
 * Blocks access if:
 * 1. Terms not accepted (handled by ProducerTermsGate)
 * 2. producer_status is 'paused' or 'banned'
 * 
 * In support preview mode (admin), gates are bypassed and a banner is shown.
 * Used in routing to wrap all /app/producer/farm/:farmId/* routes except /terms.
 */
export function ProducerPortalGate() {
  const { farmId } = useParams<{ farmId: string }>();
  const { producer, isLoading } = useProducerById(farmId);
  const { isSupportPreview } = useSupportPreview();

  // Demo mode: bypass all gates
  if (farmId === 'demo') {
    return <Outlet />;
  }

  // Support preview: bypass terms and status gates, show banner
  if (isSupportPreview) {
    return (
      <>
        <SupportPreviewBanner />
        <Outlet />
      </>
    );
  }

  // Loading state
  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background">
        <div className="w-full max-w-md px-4 space-y-4">
          <Skeleton className="h-12 w-12 rounded-full mx-auto" />
          <Skeleton className="h-8 w-3/4 mx-auto" />
          <Skeleton className="h-4 w-1/2 mx-auto" />
        </div>
      </div>
    );
  }

  // Check producer_status - block if paused or banned
  const blockedStatuses: ProducerBusinessStatus[] = ['paused', 'banned'];
  if (producer && blockedStatuses.includes(producer.producerStatus)) {
    return (
      <ProducerStatusBlock 
        producerStatus={producer.producerStatus} 
        producerName={producer.name}
      />
    );
  }

  // Normal flow: check terms first, then render outlet
  return (
    <ProducerTermsGate producerId={farmId}>
      <Outlet />
    </ProducerTermsGate>
  );
}
